<template>
  <label
    ref="root"
    tabIndex="0"
    :class="[
      $style['form-checkbox'],
      { [$style['form-checkbox--radio']]: type === 'radio' },
      { [$style['form-checkbox--bold']]: bold },
      { [$style['form-checkbox--dirty']]: validation && validation.$dirty },
      { [$style['form-checkbox--valid']]: validation && !validation.$invalid },
      { [$style['form-checkbox--disabled']]: disabled }
    ]"
    @keyup.enter="onEnter">
    <div :class="$style['form-checkbox__row']">
      <input
        :checked="checked"
        :type="type"
        :name="name"
        :class="$style['form-checkbox__input']"
        @input.prevent="onChange">

      <div
        :class="[$style['form-checkbox__icon']]" />

      <div v-if="label" :class="$style['form-checkbox__label']">
        <span v-html="label" />
        <div
          v-if="subLabel"
          :class="$style['form-checkbox__sublabel']">{{ subLabel }}</div>
      </div>
      <div v-else :class="$style['form-checkbox__label']">
        <slot />
      </div>
    </div>
    <div
      v-if="validation"
      :class="$style['form-checkbox__row']">
      <form-validation
        v-if="!hideValidationText"
        class="checkbox-validation"
        :class="$style['form-checkbox__validation']"
        :validation="validation" />
    </div>
  </label>
</template>

<script setup>
const emit = defineEmits(['update:modelValue'])
const props = defineProps({
  checked: { type: Boolean },
  bold: { type: Boolean },
  value: { type: [Number, String, Array, Object, Boolean], default: null },
  targetValue: { type: [Number, String, Object, Boolean], default: null },
  label: { type: String, default: null },
  subLabel: { type: [String, Number], default: null },
  type: { type: String, default: 'checkbox' },
  name: { type: String, default: null },
  validation: { type: [Boolean, Object], default: null },
  hideValidationText: {
    type: Boolean,
    default: false
  },

  disabled: {
    type: Boolean,
    default: false
  }
})

const root = ref(null)

const onChange = event => {
  if (props.validation) {
    props.validation.$touch()
  }
  emit('update:modelValue', event.target.checked ? props.targetValue : false)
}
const onEnter = () => {
  root.value.click()
}
</script>
<style lang="scss" module>
.form-checkbox {
  transition: opacity var(--transition-time) var(--transition-easing);

  &__row {
    line-height: 1;
    margin: var(--spacing) 0;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__icon {
    background-color: var(--white);
    border: 1px solid var(--grey-light);

    display: block;
    border-radius: calc(var(--border-radius) * 0.5);
    flex-shrink: 0;
    width: calc(var(--spacing) * 3);
    height: calc(var(--spacing) * 3);
    transition: background-color var(--transition-time) var(--transition-easing);
    position: relative;

    &::after {
      background-color: var(--white);
      border-radius: calc(var(--border-radius) * 0.5);
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transform: scale(0);
      transition: all var(--transition-time-fast) var(--transition-easing);
    }
  }

  &__label {
    align-items: center;
    justify-content: space-between;
    display: flex;
    flex-direction: row;
    font-size: var(--font-size-s);
    line-height: var(--line-height);
    margin-left: calc(var(--spacing) * 1.5);
    flex: 1;
  }

  &__sublabel {
    align-items: center;
    color: var(--grey-dark);
    display: inline-flex;
    padding-right: calc(var(--spacing) * 0.5);
    vertical-align: middle;
    transform: scale(0.9);
  }

  &:focus-visible .form-checkbox__row {
    box-shadow: var(--focus-visible);
  }

  &__input:hover ~ .form-checkbox__icon::after {
    @media (hover: hover) {
      background-color: var(--grey-light);
      transform: scale(0.6);
    }
  }

  &__input {
    visibility: hidden;
    position: absolute;
  }

  &__input:active,
  &__input:checked {
    & ~ .form-checkbox__label .form-checkbox__sublabel {
      color: var(--black);
    }

    & ~ .form-checkbox__icon::after {
      background-color: var(--black);
      transform: scale(0.6);
    }
  }

  &__validation {
    padding-left: calc(var(--spacing ) * 4);
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &--radio {
    .form-checkbox__icon {
      /* stylelint-disable */
      &,
      &::after {
        border-radius: 100%;
      }
    }
  }

  &--bold {
    font-weight: var(--font-weight-bold);
  }

  &.form-checkbox--dirty:not(.form-checkbox--valid) {
    .form-checkbox__icon {
      border-bottom-color: var(--red);
    }
  }
}
</style>
